import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { applicationActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { FileSelectDialog, InformModal } from '../../../common/SharedComponents';
import { ApplicationApi, ListApi, FileApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { RefreshEvents } from '../../../common/SharedEvents';
import AppraiserModal from '../../../appraisals/appraisers/modals/AppraiserModal';
import { LoadPanel } from 'devextreme-react/load-panel';
import { ModalSummaryHeader } from '../ApplicationComponents';
import DateBox from 'devextreme-react/date-box';
import { IncompleteReasonType } from '../../../loans/common/LoanConstants';
import { handleAdminError, handleAdminSuccess } from '../../../common/SharedFunctions';
import { ApplicationStatusType } from '@apply/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion, faEnvelope } from '@fortawesome/free-regular-svg-icons';
import { DeclineReasonType, DeclineReasonTypeOptions } from '../ApplicationConstants';

const DeclineOrRecycleModal = () => {
    const { isOpen, id } = useSelector((state) => state.application.declineOrRecycleModal);
    const { dispatch } = store;

    const [incompleteReasons, set_incompleteReasons] = useState([]);
    const [application, set_application] = useState(null);
    const [isManual, set_isManual] = useState(false);
    const [statusId, set_statusId] = useState('');
    const [reason, set_reason] = useState('');
    const [unableToVerifyIdentityDeclineReason, set_unableToVerifyIdentityDeclineReason] = useState('');
    const [withdrawalDate, set_withdrawalDate] = useState('');
    const [incompleteReasonTypeIds, set_incompleteReasonTypeIds] = useState([]);
    const [companyTaxReturnAdditionalInfo, set_companyTaxReturnAdditionalInfo] = useState('');
    const [personalTaxReturnAdditionalInfo, set_personalTaxReturnAdditionalInfo] = useState('');
    const [declineReasonTypeIds, set_declineReasonTypeIds] = useState([]);
    const [noticeDate, set_noticeDate] = useState('');
    const [noticeFile, set_noticeFile] = useState(null);
    const [sentByUserId, set_sentByUserId] = useState('');
    const [employees, set_employees] = useState([]);

    //File Upload
    const fileUploadOptions = {
        isOpen: false,
        maxFiles: 1,
        isReplace: false,
        typeName: '',
    };
    const [fileSelectOptions, set_fileSelectOptions] = useState(fileUploadOptions);
    const [loadingMessage, set_loadingMessage] = useState('');

    const [informModalTitle, set_informModalTitle] = useState('');
    const [informModalOpen, set_informModalOpen] = useState(false);
    const [informModalText, set_informModalText] = useState('');

    const applicationApi = new ApplicationApi.ApplicationApi(applicationInsights);
    const listApi = new ListApi.ListApi(applicationInsights);
    const fileApi = new FileApi.FileApi(applicationInsights);

    const refreshEvents = new RefreshEvents();

    const StatusDisplayMode = {
        Incomplete: 'Incomplete',
        NonIncomplete: 'NonIncomplete',
    };

    const appStatuses = [
        {
            id: null,
            name: 'Select...',
            statusDisplayMode: [StatusDisplayMode.Incomplete, StatusDisplayMode.NonIncomplete],
            sortOrder: 0,
        },
        {
            id: ApplicationStatusType.RecycleBin_Duplicate,
            name: 'Recycle Bin: Duplicate',
            statusDisplayMode: [StatusDisplayMode.Incomplete, StatusDisplayMode.NonIncomplete],
            sortOrder: 3,
        },
        {
            id: ApplicationStatusType.RecycleBin_MadeInError,
            name: 'Recycle Bin: Made in Error',
            statusDisplayMode: [StatusDisplayMode.Incomplete, StatusDisplayMode.NonIncomplete],
            sortOrder: 4,
        },
        {
            id: ApplicationStatusType.RecycleBin_Testing,
            name: 'Recycle Bin: Testing',
            statusDisplayMode: [StatusDisplayMode.Incomplete, StatusDisplayMode.NonIncomplete],
            sortOrder: 5,
        },
        {
            id: ApplicationStatusType.IncompleteFile,
            name: 'File Closed for Incompleteness',
            statusDisplayMode: [StatusDisplayMode.NonIncomplete],
            sortOrder: 6,
        },
        {
            id: ApplicationStatusType.Lost_WithdrawnByClient,
            name: 'No Further Action: Withdrawn by Applicant',
            statusDisplayMode: [StatusDisplayMode.NonIncomplete],
            sortOrder: 7,
        },
        {
            id: ApplicationStatusType.Lost_DeclinedByBfi,
            name: 'No Further Action: Declined by BFI',
            statusDisplayMode: [StatusDisplayMode.NonIncomplete],
            sortOrder: 8,
        },
        {
            id: ApplicationStatusType.Incomplete_ContactedWillNotBeSubmitted,
            name: 'Contacted, Will Not Be Submitted',
            statusDisplayMode: [StatusDisplayMode.Incomplete],
            sortOrder: 1,
        },
        {
            id: ApplicationStatusType.Incomplete_CouldNotContact,
            name: 'Could Not Contact',
            statusDisplayMode: [StatusDisplayMode.Incomplete],
            sortOrder: 2,
        },
    ];

    const stringSorter = (a, b) => {
        const nameA = a.toUpperCase(); // ignore upper and lowercase
        const nameB = b.toUpperCase(); // ignore upper and lowercase
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }

        // names must be equal
        return 0;
    };

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            marginRight: '10px',
            marginLeft: '10px',
            textAlign: 'right',
        },
        form: { paddingTop: '10px' },
        formRow: { marginBottom: '0' },
        formColumn: { padding: '5px' },
        iconCancel: { color: 'darkred', cursor: 'pointer' },
    };

    useEffect(() => {
        const loadListsAsync = async () => {
            await _loadBfiEmployees();
        };
        loadListsAsync();
    }, []);

    useEffect(() => {
        async function isOpenChanged() {
            resetState();

            if (!isOpen || !id) {
                return;
            }

            await loadApplication(id);
        }

        isOpenChanged();
    }, [isOpen, id]);

    useEffect(() => {
        async function applicationChanged() {
            if (!application) {
                return;
            }

            await loadIncompleteReasons(application);
            set_isManual(false);
        }

        applicationChanged();
    }, [application]);

    const setStatusId = (id) => {
        set_isManual(false);
        set_statusId(id);
    };

    const _loadBfiEmployees = async () => {
        const data = await listApi.getBfiEmployees();
        set_employees(data);
    };

    const resetState = () => {
        set_application(null);
        set_isManual(false);
        set_noticeFile(null);
        set_statusId('');
        set_reason('');
        set_unableToVerifyIdentityDeclineReason('');
        set_declineReasonTypeIds([]);
        set_incompleteReasonTypeIds([]);
        set_companyTaxReturnAdditionalInfo('');
        set_personalTaxReturnAdditionalInfo('');
        set_withdrawalDate('');
        set_noticeDate('');
        set_sentByUserId('');
    };

    const loadIncompleteReasons = async (application) => {
        try {
            set_loadingMessage('Loading...');
            const response = await listApi.getIncompleteReasonsForApplication(application.id);
            set_incompleteReasons(response.result);
        } finally {
            set_loadingMessage('');
        }
    };

    const loadApplication = async (id) => {
        try {
            set_loadingMessage('Loading...');

            const response = await applicationApi.getDeclinedReject(id);
            if (response.statusCode !== 200) {
                return;
            }

            const appl = response.result;
            set_application(appl);
        } finally {
            set_loadingMessage('');
        }
    };

    const _handleClose = () =>
        dispatch(
            applicationActions.setDeclineOrRecycleModal({
                declineOrRecycleModal: { isOpen: false },
            })
        );

    const canSubmit = () => {
        let isOk = true;
        switch (statusId) {
            case ApplicationStatusType.RecycleBin_MadeInError:
            case ApplicationStatusType.RecycleBin_Duplicate:
            case ApplicationStatusType.RecycleBin_Testing:
            case ApplicationStatusType.Incomplete_ContactedWillNotBeSubmitted:
            case ApplicationStatusType.Incomplete_CouldNotContact:
                isOk = true;
                break;
            case ApplicationStatusType.Lost_DeclinedByBfi:
                isOk = declineReasonTypeIds.length > 0;
                if (isManual) {
                    isOk = isOk && noticeDate && sentByUserId && noticeFile;
                }
                break;
            case ApplicationStatusType.IncompleteFile:
                isOk = incompleteReasonTypeIds.length > 0;
                if (isManual) {
                    isOk = isOk && noticeDate && sentByUserId && noticeFile;
                }
                break;
            case ApplicationStatusType.Lost_WithdrawnByClient:
                isOk = withdrawalDate;
                break;
        }
        return isOk;
    };

    const submitChanges = async (e) => {
        e.preventDefault();

        try {
            set_loadingMessage('Saving...');

            const model = {
                id: application.id,
                statusId,
            };

            if (isManual) {
                model.isManual = true;
                model.noticeDate = noticeDate.toLocaleDateString('en-us');
                model.sentByUserId = sentByUserId;

                if (noticeFile) {
                    set_loadingMessage(`Uploading new application files...`);
                    const upLoadResult = await fileApi.uploadTempFiles([noticeFile]);
                    let result = upLoadResult.result;
                    if (result && result.length > 0) {
                        model.noticeTempFileName = result[0].tempName;
                    }
                }
            }

            switch (statusId) {
                case ApplicationStatusType.Lost_DeclinedByBfi:
                    model.declineReasonTypeIds = declineReasonTypeIds;
                    if (declineReasonTypeIds.indexOf(DeclineReasonType.Other) > -1) {
                        model.reason = reason;
                    }
                    if (declineReasonTypeIds.indexOf(DeclineReasonType.UnableToVerifyIdentity) > -1) {
                        model.unableToVerifyIdentityDeclineReason = unableToVerifyIdentityDeclineReason;
                    }
                    break;
                case ApplicationStatusType.IncompleteFile:
                    model.incompleteReasonTypeIds = incompleteReasonTypeIds;
                    model.companyTaxReturnAdditionalInfo = companyTaxReturnAdditionalInfo;
                    model.personalTaxReturnAdditionalInfo = personalTaxReturnAdditionalInfo;
                    break;
                case ApplicationStatusType.Lost_WithdrawnByClient:
                    model.withdrawalDate = withdrawalDate.toLocaleDateString('en-us');
                    break;
            }

            const response = await applicationApi.setDeclinedReject(application.id, model);
            if (response.statusCode !== 200) {
                return;
            }

            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Changes saved successfully.');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const setIncompleteReasonTypeId = (checked, id) => {
        const index = incompleteReasonTypeIds.indexOf(id);
        if (checked) {
            if (index < 0) {
                set_incompleteReasonTypeIds([...incompleteReasonTypeIds, id]);
            }
        } else {
            if (index > -1) {
                set_incompleteReasonTypeIds(incompleteReasonTypeIds.filter((i) => i !== id));
            }
        }
    };

    const setDeclineReasonTypeId = (checked, id) => {
        const index = declineReasonTypeIds.indexOf(id);
        if (checked) {
            if (index < 0) {
                set_declineReasonTypeIds([...declineReasonTypeIds, id]);
            }
        } else if (index > -1) {
            set_declineReasonTypeIds(declineReasonTypeIds.filter((i) => i !== id));
        }
    };

    const onFileSelectSubmit = (id, files) => {
        if (!files || files.length === 0) {
            return;
        }

        set_noticeFile(files[0]);
        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    const onFileSelectCancel = () => {
        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    const openFileSelect = () => {
        const options = {
            ownerId: null,
            isOpen: true,
            maxFiles: 1,
            fileTypeName: 'Notice File',
        };
        set_fileSelectOptions(options);
    };

    const getNoticeTitle = () => {
        if (statusId === ApplicationStatusType.IncompleteFile && !isManual) {
            return "The notice 'Incomplete File' will be scheduled to be sent after Save";
        } else if (statusId === ApplicationStatusType.Lost_DeclinedByBfi && !isManual) {
            return "The notice 'Adverse Action' will be scheduled to be sent after Save";
        } else {
            return 'Current changes will not trigger a notice.';
        }
    };

    const RenderTaxReturnHelp = () => {
        return (
            <div
                style={{
                    marginLeft: '5px',
                    color: '#2196f3',
                    cursor: 'pointer',
                }}
                onClick={openTaxReturnHelp}
            >
                <FontAwesomeIcon icon={faCircleQuestion} />
            </div>
        );
    };

    const openTaxReturnHelp = () => {
        set_informModalTitle('Tax Filing');
        set_informModalText(
            "State what is missing in YYYY format along with 'return' and/or 'extension'.<br><br><b>REQUIREMENTS:</b> Most recent year AND if under extension, prior year also. Cutoff dates are April 1 and October 1 based on date application received. E.g. Application received date of April 1 – September 30, 2025 require 2024 return or 2024 extension + 2023 return. If October 1 – March 31, require 2024 return.<br><br><b>REQUIREMENTS, SBA:</b> Typically require 3 years of tax returns.</p>"
        );
        set_informModalOpen(true);
    };

    if (!isOpen || !application) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '650px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form
                                    name='declineAppraisalForm'
                                    onSubmit={(event) => submitChanges(event)}
                                >
                                    <div
                                        className='page-header'
                                        role='banner'
                                    >
                                        <h1 style={{ whiteSpace: 'nowrap' }}>Lost/Delete Application</h1>
                                        <button
                                            type='button'
                                            className='close'
                                            data-dismiss='modal'
                                            aria-label='Close'
                                            onClick={_handleClose}
                                        >
                                            <span aria-hidden='true'>&times;</span>
                                        </button>
                                    </div>

                                    <div
                                        className='row'
                                        style={styles.formRow}
                                    >
                                        <div
                                            className='columns small-12 large-12'
                                            style={{
                                                padding: '5px',
                                                textAlign: 'left',
                                                color:
                                                    (statusId === ApplicationStatusType.IncompleteFile ||
                                                        statusId === ApplicationStatusType.Lost_DeclinedByBfi) &&
                                                    !isManual
                                                        ? 'darkred'
                                                        : 'gray',
                                            }}
                                        >
                                            &nbsp;
                                            <i title={getNoticeTitle()}>
                                                <FontAwesomeIcon icon={faEnvelope} />
                                            </i>
                                            &nbsp;&nbsp;{getNoticeTitle()}
                                        </div>
                                    </div>
                                    <ModalSummaryHeader application={application} />
                                    <div
                                        className='scrollable-content'
                                        style={{ margin: '10px' }}
                                    >
                                        <div
                                            style={styles.form}
                                            className={'standard-form'}
                                        >
                                            <h2 className='decorated'>
                                                {application.properties.length === 1 ? 'Property' : 'Properties'}
                                            </h2>
                                            <ul>
                                                {application.properties.map((p) => {
                                                    return (
                                                        <li key={`p${p.id}`}>
                                                            {p.description && (
                                                                <div>
                                                                    <b>Property {p.bfiNumberSuffix}</b>
                                                                    {p.address && (
                                                                        <span>
                                                                            : {p.address?.street1} {p.address?.street2}{' '}
                                                                            {p.address?.city}, {p.address?.state}{' '}
                                                                            {p.address?.zipCode}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                            <h2 className='decorated'>Reason</h2>

                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div
                                                    className='columns small-12 large-12'
                                                    style={styles.formColumn}
                                                >
                                                    <select
                                                        id='newStatusId'
                                                        value={statusId}
                                                        onChange={(e) => setStatusId(parseInt(e.target.value))}
                                                        required
                                                    >
                                                        {appStatuses
                                                            .filter((s) =>
                                                                s.statusDisplayMode?.includes(
                                                                    application?.statusId === ApplicationStatusType.Incomplete
                                                                        ? StatusDisplayMode.Incomplete
                                                                        : StatusDisplayMode.NonIncomplete
                                                                )
                                                            )
                                                            .sort((a, b) => a.sortOrder - b.sortOrder)
                                                            .map((fs) => {
                                                                return (
                                                                    <option
                                                                        key={fs.id}
                                                                        value={fs.id}
                                                                    >
                                                                        {fs.name}
                                                                    </option>
                                                                );
                                                            })}
                                                    </select>
                                                </div>
                                            </div>
                                            {statusId === ApplicationStatusType.Lost_WithdrawnByClient && (
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div
                                                        className='columns small-12 large-3'
                                                        style={styles.formColumn}
                                                    >
                                                        <label htmlFor='withdrawalDate'>
                                                            Withdrawal Date <span className='req'>*</span>
                                                        </label>
                                                        <DateBox
                                                            id='withdrawalDate'
                                                            value={withdrawalDate ? new Date(withdrawalDate) : null}
                                                            onValueChange={set_withdrawalDate}
                                                            required={true}
                                                            placeholder='MM/DD/YYYY'
                                                            useMaskBehavior={true}
                                                            displayFormat='shortdate'
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            {(statusId === ApplicationStatusType.IncompleteFile ||
                                                statusId === ApplicationStatusType.Lost_DeclinedByBfi) && (
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div
                                                        className='columns small-12 large-12'
                                                        style={styles.formColumn}
                                                    >
                                                        <input
                                                            type='radio'
                                                            id='isAuto'
                                                            name='isManual'
                                                            value='0'
                                                            defaultChecked={isManual === false}
                                                            onClick={(e) => set_isManual(e.target.value === '1')}
                                                        />
                                                        &nbsp; <b>Automatic Notice</b>
                                                        &nbsp;&nbsp;
                                                        <input
                                                            type='radio'
                                                            id='isManual'
                                                            name='isManual'
                                                            value='1'
                                                            defaultChecked={isManual === true}
                                                            onClick={(e) => set_isManual(e.target.value === '1')}
                                                        />
                                                        &nbsp; <b>Manual Notice</b>
                                                    </div>
                                                </div>
                                            )}

                                            {statusId === ApplicationStatusType.IncompleteFile && (
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div
                                                        className='columns small-12 large-12'
                                                        style={styles.formColumn}
                                                    >
                                                        <i>An Incomplete File notice will be sent</i>
                                                        <label>
                                                            Reasons for Closing File <span className='req'>*</span>
                                                        </label>
                                                        {incompleteReasons &&
                                                            incompleteReasons.map((r, index) => {
                                                                if (r.key === IncompleteReasonType.CompanyTaxReturn) {
                                                                    return (
                                                                        <div
                                                                            key={`key_${index}`}
                                                                            style={{
                                                                                display: 'flex',
                                                                                flexDirection: 'row',
                                                                                alignItems: 'flex-start',
                                                                            }}
                                                                        >
                                                                            <input
                                                                                style={{
                                                                                    marginTop: '3px',
                                                                                }}
                                                                                id={`incompleteTypeId_${r.key}`}
                                                                                type='checkbox'
                                                                                value={r.key}
                                                                                checked={
                                                                                    incompleteReasonTypeIds.indexOf(r.key) > -1
                                                                                }
                                                                                onChange={(e) =>
                                                                                    setIncompleteReasonTypeId(
                                                                                        e.target.checked,
                                                                                        r.key
                                                                                    )
                                                                                }
                                                                            />
                                                                            &nbsp;
                                                                            {r.name}
                                                                            <input
                                                                                type='text'
                                                                                id='companyTaxReturnAdditionalInfo'
                                                                                value={companyTaxReturnAdditionalInfo}
                                                                                onChange={(e) =>
                                                                                    set_companyTaxReturnAdditionalInfo(
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                                required={false}
                                                                                disabled={false}
                                                                                maxLength={255}
                                                                                style={{
                                                                                    marginLeft: '10px',
                                                                                    width: '400px',
                                                                                }}
                                                                            />
                                                                            {RenderTaxReturnHelp()}
                                                                        </div>
                                                                    );
                                                                } else if (r.key === IncompleteReasonType.PersonalTaxReturn) {
                                                                    return (
                                                                        <div
                                                                            key={`key_${index}`}
                                                                            style={{
                                                                                display: 'flex',
                                                                                flexDirection: 'row',
                                                                                alignItems: 'flex-start',
                                                                            }}
                                                                        >
                                                                            <input
                                                                                style={{
                                                                                    marginTop: '3px',
                                                                                }}
                                                                                id={`incompleteTypeId_${r.key}`}
                                                                                type='checkbox'
                                                                                value={r.key}
                                                                                checked={
                                                                                    incompleteReasonTypeIds.indexOf(r.key) > -1
                                                                                }
                                                                                onChange={(e) =>
                                                                                    setIncompleteReasonTypeId(
                                                                                        e.target.checked,
                                                                                        r.key
                                                                                    )
                                                                                }
                                                                            />
                                                                            &nbsp;
                                                                            {r.name}
                                                                            <input
                                                                                type='text'
                                                                                id='personalTaxReturnAdditionalInfo'
                                                                                value={personalTaxReturnAdditionalInfo}
                                                                                onChange={(e) =>
                                                                                    set_personalTaxReturnAdditionalInfo(
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                                required={false}
                                                                                disabled={false}
                                                                                maxLength={255}
                                                                                style={{
                                                                                    marginLeft: '10px',
                                                                                    width: '400px',
                                                                                }}
                                                                            />
                                                                            {RenderTaxReturnHelp()}
                                                                        </div>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <div
                                                                            key={`key_${index}`}
                                                                            style={{
                                                                                display: 'flex',
                                                                                flexDirection: 'row',
                                                                                alignItems: 'flex-start',
                                                                            }}
                                                                        >
                                                                            <input
                                                                                style={{
                                                                                    marginTop: '3px',
                                                                                }}
                                                                                id={`incompleteTypeId_${r.key}`}
                                                                                type='checkbox'
                                                                                value={r.key}
                                                                                checked={
                                                                                    incompleteReasonTypeIds.indexOf(r.key) > -1
                                                                                }
                                                                                onChange={(e) =>
                                                                                    setIncompleteReasonTypeId(
                                                                                        e.target.checked,
                                                                                        r.key
                                                                                    )
                                                                                }
                                                                            />
                                                                            &nbsp;
                                                                            {r.name}
                                                                        </div>
                                                                    );
                                                                }
                                                            })}
                                                    </div>
                                                </div>
                                            )}
                                            {statusId === ApplicationStatusType.Lost_DeclinedByBfi && (
                                                <div
                                                    className='columns small-12 large-12'
                                                    style={styles.formColumn}
                                                >
                                                    <i>An Adverse Action notice will be sent</i>
                                                    <label>
                                                        Reasons for Decline
                                                        <span className='req'>*</span>
                                                    </label>
                                                    {DeclineReasonTypeOptions.sort((a, b) => a.sortOrder - b.sortOrder).map(
                                                        (r, index) => {
                                                            return (
                                                                <>
                                                                    <div
                                                                        key={`key_${index}`}
                                                                        style={{
                                                                            paddingBottom: '5px',
                                                                        }}
                                                                    >
                                                                        <input
                                                                            id={`declineReasonId_${r.id}`}
                                                                            type='checkbox'
                                                                            value={r.key}
                                                                            checked={declineReasonTypeIds.indexOf(r.key) > -1}
                                                                            onChange={(e) =>
                                                                                setDeclineReasonTypeId(e.target.checked, r.key)
                                                                            }
                                                                        />
                                                                        &nbsp;
                                                                        {r.name}
                                                                        &nbsp;
                                                                        <i
                                                                            style={{
                                                                                fontSize: 'smaller',
                                                                            }}
                                                                        >
                                                                            ({r.info})
                                                                        </i>
                                                                    </div>
                                                                    {r.key == DeclineReasonType.UnableToVerifyIdentity &&
                                                                        declineReasonTypeIds.indexOf(
                                                                            DeclineReasonType.UnableToVerifyIdentity
                                                                        ) > -1 && (
                                                                            <textarea
                                                                                style={{
                                                                                    width: '100%',
                                                                                }}
                                                                                rows={3}
                                                                                id='unableToVerifyIdentityDeclineReason'
                                                                                value={unableToVerifyIdentityDeclineReason}
                                                                                onChange={(e) =>
                                                                                    set_unableToVerifyIdentityDeclineReason(
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                                required={true}
                                                                                placeholder='Please provide more details about why the identity was unable to be verified...'
                                                                                maxLength={255}
                                                                            />
                                                                        )}
                                                                    {r.key == DeclineReasonType.Other &&
                                                                        declineReasonTypeIds.indexOf(DeclineReasonType.Other) >
                                                                            -1 && (
                                                                            <textarea
                                                                                style={{
                                                                                    width: '100%',
                                                                                }}
                                                                                rows={3}
                                                                                id='reason'
                                                                                value={reason}
                                                                                onChange={(e) => set_reason(e.target.value)}
                                                                                required={true}
                                                                                placeholder='Please provide more details about why this loan was lost...'
                                                                                maxLength={255}
                                                                            />
                                                                        )}
                                                                </>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            )}

                                            {statusId && isManual && (
                                                <>
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        {
                                                            <div
                                                                className='columns small-12 large-3'
                                                                style={styles.formColumn}
                                                            >
                                                                <label htmlFor='noticeDate'>
                                                                    Notice Date <span className='req'>*</span>
                                                                </label>
                                                                <DateBox
                                                                    id='noticeDate'
                                                                    value={noticeDate ? new Date(noticeDate) : null}
                                                                    onValueChange={set_noticeDate}
                                                                    required={true}
                                                                    placeholder='MM/DD/YYYY'
                                                                    useMaskBehavior={true}
                                                                    displayFormat='shortdate'
                                                                />
                                                            </div>
                                                        }
                                                        <div
                                                            className='columns small-12 large-3'
                                                            style={{
                                                                paddingTop: '35px',
                                                                paddingBottom: '0px',
                                                            }}
                                                        >
                                                            <button
                                                                type='button'
                                                                className='button hollow'
                                                                title='Upload Notice File'
                                                                onClick={openFileSelect}
                                                            >
                                                                Upload File
                                                            </button>
                                                            &nbsp;
                                                        </div>
                                                        <div
                                                            className='columns small-12 large-6'
                                                            style={{
                                                                marginTop: '35px',
                                                                paddingBottom: '0px',
                                                            }}
                                                        >
                                                            {noticeFile && <span>File: {noticeFile.name}</span>}
                                                        </div>
                                                    </div>
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-6'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='responsibleId'>
                                                                Notice Sent By <span className='req'>*</span>
                                                            </label>
                                                            <select
                                                                id='sentById'
                                                                value={sentByUserId}
                                                                onChange={(event) => set_sentByUserId(event.target.value)}
                                                                required={true}
                                                            >
                                                                <option
                                                                    key=''
                                                                    value=''
                                                                ></option>
                                                                {employees &&
                                                                    employees.map((poc) => {
                                                                        return (
                                                                            <option
                                                                                key={poc.key}
                                                                                value={poc.key}
                                                                            >
                                                                                {poc.name}
                                                                            </option>
                                                                        );
                                                                    })}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            type='submit'
                                            className='button primary'
                                            disabled={!canSubmit()}
                                        >
                                            Submit
                                        </button>
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={_handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
            <AppraiserModal />
            <FileSelectDialog
                options={fileSelectOptions}
                onSubmit={onFileSelectSubmit}
                onCancel={onFileSelectCancel}
            />
            <InformModal
                isOpen={informModalOpen}
                title={informModalTitle}
                text={informModalText}
                confirmCallback={() => set_informModalOpen(false)}
            />
        </>
    );
};

export default DeclineOrRecycleModal;
